<script setup lang="ts">
/**
 * General-purpose popover component.
 *
 * Wraps a trigger element (via the `#trigger` slot) and shows a styled
 * popover panel anchored to it. The panel is Teleported to `<body>` so it
 * escapes any ancestor `overflow:hidden` constraints (e.g. AG Grid cells).
 *
 * Slots:
 *   #trigger   — the element that activates the popover
 *   #default   — popover body content (required)
 *   #footer    — optional footer content; shown only when the slot is filled
 *
 * Props:
 *   title       — when supplied the header row is shown; omit to hide header
 *   placement   — which side to open on (top | bottom | left | right)
 *   trigger     — activation event(s): "hover" | "click" | "focus" or array
 *   width       — explicit width (px number or CSS string); auto if omitted
 *   offset      — gap in px between trigger edge and popover (default 8)
 *   disabled    — when true the popover never opens
 *
 * Usage (hover):
 *   <AppPopover title="Info" placement="top" :width="240">
 *     <template #trigger><span class="my-pill">PO-123</span></template>
 *     <p>Some content</p>
 *   </AppPopover>
 */

import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  useId,
  useSlots,
} from "vue";

type Placement = "top" | "bottom" | "left" | "right";
type TriggerType = "hover" | "click" | "focus";

const props = withDefaults(
  defineProps<{
    title?: string;
    placement?: Placement;
    trigger?: TriggerType | TriggerType[];
    width?: number | string;
    offset?: number;
    disabled?: boolean;
    /** A table row can be the trigger without inserting invalid spans in tbody. */
    as?: "span" | "tr";
    tooltip?: boolean;
    /** Optional overlay owner, for nested non-modal settings surfaces. */
    teleportTo?: string | HTMLElement;
  }>(),
  {
    placement: "top",
    trigger: "hover",
    offset: 8,
    disabled: false,
    as: "span",
    tooltip: false,
    teleportTo: "body",
  }
);

const slots = useSlots();
const hasTitle = computed(() => Boolean(props.title?.trim()));
const hasFooter = computed(() => Boolean(slots.footer));

const visible = ref(false);
const tooltipId = useId();
const triggerRef = ref<HTMLElement | null>(null);
const popoverRef = ref<HTMLElement | null>(null);
let hideTimer: ReturnType<typeof setTimeout> | null = null;

// Start offscreen so the teleported element doesn't flash at (0,0)
const popoverStyle = ref<Record<string, string>>({
  top: "-9999px",
  left: "-9999px",
});

function position(): void {
  if (!triggerRef.value || !popoverRef.value) return;

  // position: fixed — coordinates are viewport-relative (no scroll offset needed)
  const triggerRect = triggerRef.value.getBoundingClientRect();
  const popoverRect = popoverRef.value.getBoundingClientRect();
  const gap = props.offset;

  let top = 0;
  let left = 0;

  switch (props.placement) {
    case "bottom": {
      top = triggerRect.bottom + gap;
      left = triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2;
      break;
    }
    case "left": {
      top = triggerRect.top + triggerRect.height / 2 - popoverRect.height / 2;
      left = triggerRect.left - popoverRect.width - gap;
      break;
    }
    case "right": {
      top = triggerRect.top + triggerRect.height / 2 - popoverRect.height / 2;
      left = triggerRect.right + gap;
      break;
    }
    default: {
      // "top"
      top = triggerRect.top - popoverRect.height - gap;
      left = triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2;
    }
  }

  // Clamp to viewport so the popover never escapes the visible area
  const margin = 8;
  left = Math.max(
    margin,
    Math.min(left, window.innerWidth - popoverRect.width - margin)
  );
  top = Math.max(
    margin,
    Math.min(top, window.innerHeight - popoverRect.height - margin)
  );

  const style: Record<string, string> = {
    top: `${String(top)}px`,
    left: `${String(left)}px`,
  };

  if (props.width != null) {
    style.width =
      typeof props.width === "number"
        ? `${String(props.width)}px`
        : props.width;
  }

  popoverStyle.value = style;
}

async function show(): Promise<void> {
  if (props.disabled) return;
  cancelHide();
  visible.value = true;
  await nextTick();
  position();
}

function cancelHide(): void {
  if (hideTimer !== null) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
}

function scheduleHide(): void {
  cancelHide();
  hideTimer = setTimeout(() => {
    visible.value = false;
    hideTimer = null;
  }, 120);
}

function toggle(): void {
  if (visible.value) {
    scheduleHide();
  } else {
    void show();
  }
}

const activeTriggers = computed<TriggerType[]>(() =>
  Array.isArray(props.trigger) ? props.trigger : [props.trigger]
);

function onTriggerMouseenter(): void {
  if (activeTriggers.value.includes("hover")) void show();
}
function onTriggerMouseleave(): void {
  if (activeTriggers.value.includes("hover")) scheduleHide();
}
function onPopoverMouseenter(): void {
  if (activeTriggers.value.includes("hover")) cancelHide();
}
function onPopoverMouseleave(): void {
  if (activeTriggers.value.includes("hover")) scheduleHide();
}
function onClick(): void {
  if (activeTriggers.value.includes("click")) toggle();
}
function onFocusin(): void {
  if (activeTriggers.value.includes("focus")) void show();
}
function onFocusout(): void {
  if (activeTriggers.value.includes("focus")) scheduleHide();
}

function dismissOnEscape(event: KeyboardEvent): void {
  if (event.key === "Escape" && visible.value) {
    cancelHide();
    visible.value = false;
  }
}

function dismissOnOutsidePointer(event: PointerEvent): void {
  const target = event.target;
  if (
    !visible.value ||
    !(target instanceof Node) ||
    triggerRef.value?.contains(target) ||
    popoverRef.value?.contains(target)
  ) {
    return;
  }
  cancelHide();
  visible.value = false;
}

onMounted(() => {
  document.addEventListener("keydown", dismissOnEscape);
  document.addEventListener("pointerdown", dismissOnOutsidePointer);
});
onUnmounted(() => {
  document.removeEventListener("keydown", dismissOnEscape);
  document.removeEventListener("pointerdown", dismissOnOutsidePointer);
  cancelHide();
  visible.value = false;
});

// Exported only so the type is explicit for consumers — not part of the API.
defineExpose<{ show: () => Promise<void>; hide: () => void }>({
  show,
  hide: scheduleHide,
});
</script>

<template>
  <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
  <component
    :is="as"
    ref="triggerRef"
    :class="as === 'span' ? 'appopover-trigger' : undefined"
    :tabindex="tooltip ? 0 : undefined"
    :aria-describedby="tooltip && visible ? tooltipId : undefined"
    @mouseenter="onTriggerMouseenter"
    @mouseleave="onTriggerMouseleave"
    @click="onClick"
    @focusin="onFocusin"
    @focusout="onFocusout"
  >
    <slot
      name="trigger"
      :show="show"
      :tooltip-id="visible ? tooltipId : undefined"
    />

    <Teleport :to="teleportTo">
      <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
      <div
        v-if="visible"
        :id="tooltip ? tooltipId : undefined"
        ref="popoverRef"
        class="appopover"
        :role="tooltip ? 'tooltip' : undefined"
        :data-placement="placement"
        :style="[
          popoverStyle,
          {
            width: typeof width === 'number' ? `${width}px` : width,
            maxWidth: 'calc(100vw - 16px)',
          },
        ]"
        @mouseenter="onPopoverMouseenter"
        @mouseleave="onPopoverMouseleave"
      >
        <div v-if="hasTitle" class="appopover-header">{{ title }}</div>
        <div class="appopover-body">
          <slot />
        </div>
        <div v-if="hasFooter" class="appopover-footer">
          <slot name="footer" />
        </div>
      </div>
    </Teleport>
  </component>
</template>
